import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import type { PortalTarget } from '../store/projectStore';
import type { WorkspaceTarget } from '../electron-shared/workspace/workspace-descriptor';

export function useOpenWorkspace({
  tenantId,
  tenantName,
}: {
  tenantId: string | null;
  tenantName: string;
}) {
  const t = useTranslations('proxy.workspaces');

  const openWorkspaceTarget = useCallback(
    async (target: WorkspaceTarget): Promise<void> => {
      const workspaceApi = window.electronAPI?.workspace;

      if (!workspaceApi) {
        toasterApi.error({
          title: t('openFailed'),
          description: t('notReady'),
        });

        return;
      }

      const result = await workspaceApi.open(target);

      if (!result.success) {
        toasterApi.error({ title: t('openFailed'), description: result.error });
      }
    },
    [t],
  );

  const toWorkspaceTarget = useCallback(
    (target: PortalTarget): WorkspaceTarget | null => {
      const workspaceTenantId = target.tenantId ?? tenantId;

      return workspaceTenantId
        ? {
            tenantId: workspaceTenantId,
            tenantName,
            targetId: target.id,
            targetType: target.targetType,
            targetName: target.name,
          }
        : null;
    },
    [tenantId, tenantName],
  );

  const openWorkspace = useCallback(
    async (target: PortalTarget): Promise<void> => {
      const workspaceTarget = toWorkspaceTarget(target);

      if (!workspaceTarget) {
        toasterApi.error({
          title: t('openFailed'),
          description: t('notReady'),
        });

        return;
      }

      await openWorkspaceTarget(workspaceTarget);
    },
    [openWorkspaceTarget, t, toWorkspaceTarget],
  );

  const focusTargetElsewhere = useCallback(
    async (target: PortalTarget): Promise<boolean> => {
      const workspaceApi = window.electronAPI?.workspace;
      const workspaceTarget = toWorkspaceTarget(target);

      if (!workspaceApi || !workspaceTarget) {
        return false;
      }

      const result = await workspaceApi
        .focusTargetElsewhere(workspaceTarget)
        .catch((error: unknown) => ({
          success: false as const,
          error: error instanceof Error ? error.message : String(error),
        }));

      if (result.success) {
        return result.data;
      }

      console.warn(
        '[useOpenWorkspace] Could not check other windows; switching this window instead',
        { error: new Error(result.error) },
      );

      return false;
    },
    [toWorkspaceTarget],
  );

  const openPicker = useCallback(async (): Promise<void> => {
    await window.electronAPI?.workspace?.openPicker();
  }, []);

  return {
    openWorkspace,
    openWorkspaceTarget,
    focusTargetElsewhere,
    openPicker,
  };
}
